import type { Attachment } from "@/types/attachment";

export interface ValidationResult {
  valid: Attachment[];
  errors: {
    oversized: boolean;
    tooMany: boolean;
  };
}

export interface ValidationLimits {
  maxTotalSize: number;
  maxFileCount: number;
}

/**
 * 验证附件列表（纯函数，可独立测试）
 * @param attachments 附件列表
 * @param limits 限制条件
 * @returns 验证结果
 */
export function validateAttachments(
  attachments: Attachment[],
  limits: ValidationLimits
): ValidationResult {
  let totalSize = 0;
  let oversized = false;
  const valid: Attachment[] = [];

  // 检查大小限制
  for (const item of attachments) {
    if (item.type === "url") {
      valid.push(item);
      continue;
    }

    const fileSize = item.file.size || 0;
    if (totalSize + fileSize <= limits.maxTotalSize) {
      valid.push(item);
      totalSize += fileSize;
    } else {
      oversized = true;
    }
  }

  // 检查数量限制
  const tooMany = valid.length > limits.maxFileCount;
  const limitedValid = valid.slice(0, limits.maxFileCount);

  return {
    valid: limitedValid,
    errors: {
      oversized,
      tooMany,
    },
  };
}

/**
 * 为附件生成预览（纯函数部分）
 * @param attachment 附件
 * @param previewGenerator 预览生成器（注入依赖）
 * @returns 带预览的附件
 */
export async function ensureAttachmentPreview(
  attachment: Attachment,
  previewGenerator: (file: File) => Promise<string>
): Promise<Attachment> {
  if (attachment.preview) {
    return attachment;
  }

  if (attachment.type === "file") {
    const preview = await previewGenerator(attachment.file);
    return { ...attachment, preview };
  } else {
    return { ...attachment, preview: attachment.url };
  }
}
